import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Check, CheckCircle2, Clock, FileText, Hash, Hourglass, Loader2, Mail, MapPin, Network, Phone, Receipt,
  ShieldCheck, User, X, XCircle,
} from "lucide-react";
import { api, apiError, API, getToken } from "@/lib/api";
import { formatPrice, formatDateTime } from "@/lib/format";
import { AdminCard, AdminModal, Field, Pill, textareaCls } from "@/pages/admin/ui";
import WhatsAppLink from "@/components/WhatsAppLink";

function ProofButton({ orderId }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [isPdf, setIsPdf] = useState(false);
  const [loading, setLoading] = useState(false);

  const view = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/orders/${orderId}/payment-proof`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("not found");
      const blob = await res.blob();
      setIsPdf(blob.type.includes("pdf"));
      setUrl(URL.createObjectURL(blob));
      setOpen(true);
    } catch (e) {
      toast.error("Preuve introuvable");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid={`proof-view-${orderId}`}
        onClick={view}
        disabled={loading}
        className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs font-bold text-ink hover:border-brand hover:text-brand disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
        Voir la preuve
      </button>
      <AdminModal open={open} onOpenChange={(v) => { setOpen(v); if (!v && url) { URL.revokeObjectURL(url); setUrl(""); } }} title="Preuve de paiement">
        {url && (isPdf ? (
          <iframe title="preuve" src={url} className="h-[70vh] w-full rounded-lg border border-line" />
        ) : (
          <img src={url} alt="Preuve de paiement" className="mx-auto max-h-[70vh] rounded-lg" />
        ))}
      </AdminModal>
    </>
  );
}

function Row({ icon: Icon, label, value, testId }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-xs" data-testid={testId}>
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
      <span className="text-slate-500">{label} :</span>
      <span className="min-w-0 flex-1 break-words font-bold text-ink">{value}</span>
    </div>
  );
}

export default function AdminPaymentsVerify() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");

  const load = () =>
    api.get("/admin/payments/pending-list")
      .then(({ data }) => setOrders(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const confirm = async (order) => {
    if (!window.confirm(`Confirmer le paiement de la commande ${order.order_number} ?`)) return;
    setBusy(order.id);
    try {
      await api.post(`/admin/orders/${order.id}/payment/verify`, { action: "confirm", note: "" });
      toast.success("Paiement confirmé");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const reject = async () => {
    if (!rejectFor) return;
    setBusy(rejectFor.id);
    try {
      await api.post(`/admin/orders/${rejectFor.id}/payment/verify`, { action: "reject", note: reason.trim() });
      toast.success("Paiement refusé");
      setRejectFor(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-5">
      <AdminCard
        title="💳 Paiements à vérifier"
        description="Paiements manuels et QR déclarés par les clients, en attente de votre validation. La licence n'est livrée qu'après confirmation."
        action={<Pill tone="amber">{orders.length} en attente</Pill>}
      >
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : orders.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-line p-12 text-center" data-testid="pv-empty">
            <CheckCircle2 className="mx-auto h-9 w-9 text-emerald-300" />
            <p className="mt-3 text-sm font-bold text-ink">Aucun paiement en attente</p>
            <p className="mt-1 text-xs text-slate-500">Tous les paiements déclarés ont été traités.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="pv-list">
            {orders.map((order, i) => {
              const claim = order.payment_claim || {};
              const product = (order.items || [])[0];
              const more = (order.items || []).length - 1;
              return (
                <div key={order.id} data-testid={`pv-card-${i}`} className="rounded-2xl border border-line bg-white p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line pb-3">
                    <div className="flex items-center gap-3">
                      {claim.method_logo ? (
                        <img src={claim.method_logo.startsWith("http") || claim.method_logo.startsWith("/api/") ? claim.method_logo : claim.method_logo} alt="" className="h-11 w-11 rounded-xl border border-line object-contain p-1" />
                      ) : (
                        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-light text-brand"><Receipt className="h-5 w-5" /></span>
                      )}
                      <div>
                        <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
                          <Link to={`/admin/commandes/${order.id}`} className="hover:text-brand hover:underline">{order.order_number}</Link>
                          <Pill tone="amber">En vérification</Pill>
                        </p>
                        <p className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-500">
                          <Clock className="h-3 w-3" /> {formatDateTime(claim.submitted_at || order.created_at)}
                        </p>
                      </div>
                    </div>
                    <p className="font-display text-2xl font-black text-brand" data-testid={`pv-amount-${i}`}>
                      {formatPrice(order.total, "XOF")}
                    </p>
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Row icon={User} label="Client" value={order.customer?.name} testId={`pv-client-${i}`} />
                      <Row icon={Mail} label="Email" value={order.customer?.email} testId={`pv-email-${i}`} />
                      <Row icon={Receipt} label="Produit" value={`${product?.name || "—"}${more > 0 ? ` + ${more} autre(s)` : ""}`} />
                    </div>
                    <div className="space-y-1.5">
                      <Row icon={ShieldCheck} label="Moyen" value={claim.method_name} testId={`pv-method-${i}`} />
                      <Row icon={Phone} label="Numéro utilisé" value={claim.phone ? <WhatsAppLink phone={claim.phone} /> : (claim.payment_number || null)} />
                      <Row icon={Network} label="Réseau" value={claim.network} />
                      <Row icon={MapPin} label="Adresse" value={claim.address} />
                      <Row icon={Hash} label="ID / TXID" value={claim.transaction_id} testId={`pv-txid-${i}`} />
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
                    <div className="flex items-center gap-2">
                      {claim.proof_path ? (
                        <ProofButton orderId={order.id} />
                      ) : (
                        <span className="text-xs text-slate-400">Aucune preuve fournie</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`pv-reject-${i}`}
                        onClick={() => { setRejectFor(order); setReason(""); }}
                        disabled={busy === order.id}
                        className="flex items-center gap-1.5 rounded-full border border-rose-200 px-4 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 disabled:opacity-60"
                      >
                        <XCircle className="h-4 w-4" /> Refuser le paiement
                      </button>
                      <button
                        data-testid={`pv-confirm-${i}`}
                        onClick={() => confirm(order)}
                        disabled={busy === order.id}
                        className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-5 py-2 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-60"
                      >
                        {busy === order.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                        Valider le paiement
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </AdminCard>

      <AdminModal open={!!rejectFor} onOpenChange={(v) => !v && setRejectFor(null)} title="Refuser le paiement">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Commande <span className="font-bold text-ink">{rejectFor?.order_number}</span> — indiquez le motif du refus (visible par le client).
          </p>
          <Field label="Motif du refus">
            <textarea
              data-testid="pv-reject-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex : Montant reçu incorrect, ID de transaction introuvable…"
              className={textareaCls}
            />
          </Field>
          <div className="flex justify-end gap-3 border-t border-line pt-4">
            <button onClick={() => setRejectFor(null)} className="rounded-full border border-line px-5 py-2.5 text-xs font-bold text-ink hover:border-brand hover:text-brand">
              Annuler
            </button>
            <button
              data-testid="pv-reject-confirm"
              onClick={reject}
              disabled={busy === rejectFor?.id}
              className="flex items-center gap-2 rounded-full bg-rose-600 px-6 py-2.5 text-xs font-bold text-white hover:bg-rose-700 disabled:opacity-60"
            >
              {busy === rejectFor?.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
              Confirmer le refus
            </button>
          </div>
        </div>
      </AdminModal>
    </div>
  );
}
